/**
 * Utility functions for working with dynamic pricing tiers
 */

/**
 * Parse pricing tiers from account settings
 * Supports both new JSON format and legacy tier1/2/3 fields
 */
export const parsePricingTiers = (accountSettings) => {
  if (!accountSettings) return [];

  // Try to parse from JSON first
  if (accountSettings.pricingTiersJson) {
    try {
      const tiers = JSON.parse(accountSettings.pricingTiersJson);
      if (Array.isArray(tiers) && tiers.length > 0) {
        // Sort by minQty descending for proper tier matching
        return tiers
          .filter(t => t.minQty && t.discount)
          .sort((a, b) => b.minQty - a.minQty);
      }
    } catch (e) {
      console.error("Failed to parse pricing tiers JSON:", e);
    }
  }

  // Fall back to legacy tier fields
  const legacyTiers = [];
  if (accountSettings.tier1Qty && accountSettings.tier1Discount) {
    legacyTiers.push({
      id: "tier1",
      name: accountSettings.tier1Name || "Tier 1",
      minQty: accountSettings.tier1Qty,
      discount: parseFloat(accountSettings.tier1Discount)
    });
  }
  if (accountSettings.tier2Qty && accountSettings.tier2Discount) {
    legacyTiers.push({
      id: "tier2",
      name: accountSettings.tier2Name || "Tier 2",
      minQty: accountSettings.tier2Qty,
      discount: parseFloat(accountSettings.tier2Discount)
    });
  }
  if (accountSettings.tier3Qty && accountSettings.tier3Discount) {
    legacyTiers.push({
      id: "tier3",
      name: accountSettings.tier3Name || "Tier 3",
      minQty: accountSettings.tier3Qty,
      discount: parseFloat(accountSettings.tier3Discount)
    });
  }

  // Sort by minQty descending
  return legacyTiers.sort((a, b) => b.minQty - a.minQty);
};

/**
 * Get the applicable discount for a given quantity
 * @param {Object} job - The job/order object (may have discount override)
 * @param {Object} accountSettings - Account settings with pricing tiers
 * @param {number} quantity - Total quantity to check against tiers
 * @returns {Object} - { discount: number, tier: object|null, label: string|null }
 */
export const getAppliedDiscount = (job, accountSettings, quantity) => {
  // If job has a specific discount override set
  if (job?.discountType && job.discountType !== "AUTO") {
    if (job.discountType === "NONE") {
      return { discount: 0, tier: null, label: "No Discount" };
    }
    if (job.discountType === "CUSTOM" && job.discountPercent) {
      return {
        discount: parseFloat(job.discountPercent),
        tier: null,
        label: job.discountLabel || "Custom"
      };
    }
    if (job.discountType === "BASE") {
      const base = accountSettings?.baseWholesaleDiscount;
      if (base) {
        return {
          discount: parseFloat(base),
          tier: null,
          label: "Base Discount"
        };
      }
      return { discount: 0, tier: null, label: "Base Discount (not configured)" };
    }

    // Check if it's a specific tier ID selected
    const tiers = parsePricingTiers(accountSettings);
    const selectedTier = tiers.find(t => t.id === job.discountType);
    if (selectedTier) {
      return {
        discount: parseFloat(selectedTier.discount),
        tier: selectedTier,
        label: selectedTier.name
      };
    }

    // Legacy tier type handling (TIER1, TIER2, TIER3)
    if (job.discountType === "TIER1" && accountSettings?.tier1Discount) {
      return {
        discount: parseFloat(accountSettings.tier1Discount),
        tier: { id: "tier1" },
        label: accountSettings.tier1Name || "Tier 1"
      };
    }
    if (job.discountType === "TIER2" && accountSettings?.tier2Discount) {
      return {
        discount: parseFloat(accountSettings.tier2Discount),
        tier: { id: "tier2" },
        label: accountSettings.tier2Name || "Tier 2"
      };
    }
    if (job.discountType === "TIER3" && accountSettings?.tier3Discount) {
      return {
        discount: parseFloat(accountSettings.tier3Discount),
        tier: { id: "tier3" },
        label: accountSettings.tier3Name || "Tier 3"
      };
    }
  }

  // No AUTO mode - discount must be manually selected
  // If no discountType is set, return no discount
  return { discount: 0, tier: null, label: null };
};

/**
 * Get information about the next tier threshold
 * @param {Object} job - The job/order object
 * @param {Object} accountSettings - Account settings with pricing tiers
 * @param {number} currentQuantity - Current total quantity
 * @returns {Object|null} - { needed: number, discount: number, name: string } or null
 */
export const getNextTierInfo = (job, accountSettings, currentQuantity) => {
  // If discount is manually set (not AUTO), don't show next tier hint
  if (job?.discountType && job.discountType !== "AUTO") return null;
  if (!accountSettings) return null;

  const tiers = parsePricingTiers(accountSettings);

  // Tiers are sorted by minQty descending, so reverse to go ascending
  const tiersAscending = [...tiers].reverse();

  for (const tier of tiersAscending) {
    if (currentQuantity < tier.minQty) {
      return {
        needed: tier.minQty - currentQuantity,
        discount: tier.discount,
        name: tier.name
      };
    }
  }

  return null;
};

/**
 * Build discount options for the discount selector dropdown
 * @param {Object} accountSettings - Account settings with pricing tiers
 * @returns {Array} - Array of { type, label, discount } options
 */
export const buildDiscountOptions = (accountSettings) => {
  const options = [
    { type: "NONE", label: "No Discount", discount: 0 },
  ];

  if (accountSettings?.baseWholesaleDiscount) {
    options.push({
      type: "BASE",
      label: `Base Discount (${accountSettings.baseWholesaleDiscount}%)`,
      discount: accountSettings.baseWholesaleDiscount
    });
  }

  const tiers = parsePricingTiers(accountSettings);
  // Sort ascending for display
  const tiersAscending = [...tiers].reverse();

  for (const tier of tiersAscending) {
    options.push({
      type: tier.id,
      label: `${tier.name} (${tier.discount}% - min ${tier.minQty} units)`,
      discount: tier.discount
    });
  }

  options.push({ type: "CUSTOM", label: "Custom Discount", discount: null });

  return options;
};
